import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import * as pdfjsLib from "pdfjs-dist";
import { X, ZoomIn, ZoomOut, Download, RefreshCw } from "lucide-react";

// Worker pdf.js SERVITO VERBATIM da /public (NON l'asset emesso da webpack): CRA/craco passa l'asset del
// worker attraverso babel (absoluteRuntime) lasciandovi import ESM con path assoluti della macchina di build,
// così in produzione il module worker muore in fase di valutazione e getDocument non si risolve mai (era la
// causa reale del caricamento infinito, riportato come "file mancante o danneggiato"). Il file in /public è la
// copia non trasformata di pdfjs-dist/build/pdf.worker.min.mjs; lo carichiamo via fetch+Blob (MIME-independent).
const WORKER_URL = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

let _worker = null, _workerPromise = null;
async function ensureWorker() {
  if (_worker) return _worker;
  if (!_workerPromise) {
    _workerPromise = (async () => {
      const res = await fetch(WORKER_URL);
      if (!res.ok) throw new Error("worker-fetch");
      const code = await res.text();
      if (/^\s*<(?:!doctype|html)/i.test(code)) throw new Error("worker-html");
      const blobUrl = URL.createObjectURL(new Blob([code], { type: "text/javascript" }));
      const w = new Worker(blobUrl, { type: "module" });
      // Se il worker muore (errore di valutazione) lo intercettiamo per non restare bloccati sul caricamento
      w.__dead = new Promise((_, reject) => {
        const fail = () => reject(new Error("worker-dead"));
        w.addEventListener("error", fail, { once: true });
        w.addEventListener("messageerror", fail, { once: true });
      });
      w.__dead.catch(() => {});
      _worker = w;
      pdfjsLib.GlobalWorkerOptions.workerPort = w;
      return w;
    })().catch((e) => { _workerPromise = null; throw e; });
  }
  return _workerPromise;
}
function teardownWorker() {
  try { _worker?.terminate(); } catch { /* noop */ }
  _worker = null; _workerPromise = null;
  pdfjsLib.GlobalWorkerOptions.workerPort = null;
}

function Page({ pdf, num, scale, width }) {
  const ref = useRef(null);
  useEffect(() => {
    let task = null, cancelled = false;
    (async () => {
      const page = await pdf.getPage(num);
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      const fit = (width - 16) / base.width;
      const vp = page.getViewport({ scale: fit * scale });
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const c = ref.current; if (!c) return;
      c.width = Math.floor(vp.width * dpr); c.height = Math.floor(vp.height * dpr);
      c.style.width = `${Math.floor(vp.width)}px`; c.style.height = `${Math.floor(vp.height)}px`;
      const ctx = c.getContext("2d");
      task = page.render({ canvasContext: ctx, viewport: vp, transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null });
      try { await task.promise; } catch { /* annullato */ }
    })();
    return () => { cancelled = true; task?.cancel(); };
  }, [pdf, num, scale, width]);
  return <canvas ref={ref} className="bg-white shadow mx-auto block max-w-full" data-testid="pdf-page" data-page={num} />;
}

// Visualizzatore PDF interno: riceve un Blob già scaricato con autenticazione, valida i byte %PDF- e
// renderizza tutte le pagine su canvas (funziona su Safari iOS/PWA e Android, dove <iframe> non mostra il PDF).
export function PdfViewer({ blob, filename, onClose, testid = "pdf-viewer" }) {
  const { t } = useTranslation();
  const [pdf, setPdf] = useState(null);
  const [err, setErr] = useState(null);
  const [scale, setScale] = useState(1);
  const [width, setWidth] = useState(() => Math.min(window.innerWidth, 900));
  const [attempt, setAttempt] = useState(0); // per il pulsante Riprova
  const bodyRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    let doc = null, cancelled = false, timer = null, task = null;
    setPdf(null); setErr(null);
    (async () => {
      try {
        if (!blob || blob.size === 0) throw new Error("empty");
        const buf = await blob.arrayBuffer();
        // Rifiuta esplicitamente risposte non-PDF (HTML/JSON di errore o file corrotto)
        const sig = String.fromCharCode.apply(null, new Uint8Array(buf.slice(0, 5)));
        if (!sig.startsWith("%PDF")) throw new Error("not-pdf");
        const w = await ensureWorker();
        task = pdfjsLib.getDocument({ data: buf, isEvalSupported: false });
        // Watchdog: worker morto o nessuna risposta → non restare bloccati sul caricamento infinito
        const timeout = new Promise((_, rej) => { timer = setTimeout(() => rej(new Error("timeout")), 15000); });
        doc = await Promise.race([task.promise, w.__dead, timeout]);
        if (!cancelled) setPdf(doc);
      } catch (e) {
        if (cancelled) return;
        const m = e?.name === "PasswordException" ? "protected" : e?.message;
        // worker morto/timeout/fetch: ricrea il worker al prossimo "Riprova"
        if (["worker-dead", "timeout", "worker-fetch", "worker-html"].includes(m)) teardownWorker();
        setErr(m === "protected" ? t("pdfx.err_protected")
          : m === "not-pdf" ? t("pdfx.err_invalid")
          : ["worker-fetch", "worker-html", "worker-dead", "timeout"].includes(m) ? t("pdfx.err_network")
          : t("pdfx.err_corrupt"));
      } finally { if (timer) clearTimeout(timer); }
    })();
    return () => { cancelled = true; if (timer) clearTimeout(timer); try { task?.destroy(); } catch { /* noop */ } try { doc?.destroy(); } catch { /* noop */ } };
  }, [blob, t, attempt]);

  useEffect(() => {
    const el = bodyRef.current;
    const measure = () => setWidth(Math.min(el?.clientWidth || window.innerWidth, 900));
    measure(); window.addEventListener("resize", measure);
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("resize", measure); window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const download = useCallback(() => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename || "documento.pdf";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }, [blob, filename]);

  const canDownload = !!blob && blob.size > 0;

  return createPortal(
    <div className="fixed inset-0 z-[1000] bg-slate-900 flex flex-col" role="dialog" aria-modal="true" aria-label={filename} data-testid={testid}
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="flex items-center gap-2 p-2 text-white bg-slate-800 shrink-0">
        <button ref={closeRef} className="p-2 rounded hover:bg-slate-700" onClick={onClose} aria-label={t("pdfx.close")} data-testid={`${testid}-close`}><X size={20} /></button>
        <span className="text-sm truncate flex-1" data-testid={`${testid}-title`}>{filename}</span>
        {pdf && <span className="text-xs text-slate-300 hidden sm:inline" data-testid={`${testid}-pages`}>{t("pdfx.pages", { n: pdf.numPages })}</span>}
        <button className="p-2 rounded hover:bg-slate-700 disabled:opacity-40" onClick={() => setScale(s => Math.max(0.5, +(s - 0.25).toFixed(2)))} disabled={!pdf || scale <= 0.5} aria-label={t("pdfx.zoom_out")} data-testid={`${testid}-zoom-out`}><ZoomOut size={20} /></button>
        <span className="text-xs w-10 text-center" data-testid={`${testid}-zoom`}>{Math.round(scale * 100)}%</span>
        <button className="p-2 rounded hover:bg-slate-700 disabled:opacity-40" onClick={() => setScale(s => Math.min(3, +(s + 0.25).toFixed(2)))} disabled={!pdf || scale >= 3} aria-label={t("pdfx.zoom_in")} data-testid={`${testid}-zoom-in`}><ZoomIn size={20} /></button>
        <button className="p-2 rounded bg-white text-slate-900 font-medium text-xs flex items-center gap-1 hover:bg-slate-200 disabled:opacity-40" onClick={download} disabled={!canDownload} data-testid={`${testid}-download`}><Download size={14} /> <span className="hidden sm:inline">{t("pdfx.download")}</span></button>
      </div>
      <div ref={bodyRef} className="flex-1 overflow-auto p-2 space-y-3" style={{ WebkitOverflowScrolling: "touch" }} data-testid={`${testid}-body`}>
        {!pdf && !err && <div className="text-slate-200 text-center py-10" role="status" data-testid={`${testid}-loading`}>{t("pdfx.loading")}</div>}
        {err && <div className="text-center py-10 px-4 max-w-md mx-auto" role="alert" data-testid={`${testid}-error`}>
          <p className="text-red-300 mb-5">{err}</p>
          <div className="flex flex-wrap gap-2 justify-center">
            <button className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-colors"
              onClick={() => setAttempt(a => a + 1)} data-testid={`${testid}-retry`}><RefreshCw size={16} /> {t("pdfx.retry")}</button>
            {canDownload && <button className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-white text-slate-900 font-medium hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-colors"
              onClick={download} data-testid={`${testid}-error-download`}><Download size={16} /> {t("pdfx.download")}</button>}
            <button className="inline-flex items-center px-4 py-2 rounded-lg border border-white/60 text-white font-medium hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-colors"
              onClick={onClose} data-testid={`${testid}-error-close`}>{t("pdfx.close")}</button>
          </div>
        </div>}
        {pdf && Array.from({ length: pdf.numPages }, (_, i) => <Page key={i + 1} pdf={pdf} num={i + 1} scale={scale} width={width} />)}
      </div>
    </div>, document.body);
}

// Hook condiviso: scarica il PDF con l'header Authorization (interceptor axios) e gestisce stato/errore.
export function usePdfOpener(api) {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null); // { blob, filename }
  const [loading, setLoading] = useState(null);
  const open = async (url, filename, key = url) => {
    setLoading(key);
    try {
      const { data, headers } = await api.get(url, { responseType: "blob" });
      const ct = (headers["content-type"] || data.type || "").split(";")[0];
      const blob = new Blob([data], { type: ct || "application/pdf" });
      setDoc({ blob, filename, contentType: ct });
      return blob;
    } catch (e) {
      const st = e?.response?.status;
      throw new Error(st === 401 || st === 403 ? t("pdfx.err_forbidden") : st === 404 ? t("pdfx.err_missing") : t("pdfx.err_network"));
    } finally { setLoading(null); }
  };
  return { doc, setDoc, loading, open, close: () => setDoc(null) };
}
